// risk.js — Risk Overview (dedicated page, single source of truth)
// Provides: renderRisk
// Consolidates the five risk domains into one page, consuming pure formulas from core/risk-calc.js
// (no duplicated math): Concentration · Drawdown & Volatility · Leverage & Open-trade risk ·
// FX exposure · Allocation drift. Render is pure: no save(), no cloud writes.
// v2 SHELL PORT (SPEKTRX_Risk_Mockup_v0.1, approved 2026-07-31): mny-hd text header +
// separate score panel (score drawn twice) → ONE .neo-hero (score + domain bars + ring);
// 5-cell .mny-kpis → Trades grid4 (drift lives in its queue row); every interior surface
// glass (.gls-soft / .gls-track) — no opaque var(--bg*) fills. Data flow unchanged.

import { f, fU, esc } from '../core/utils.js';
import {
  riskConcentration, riskDrawdownVol, riskOpenTrades, riskFxExposure, riskAllocationDrift,
  riskScore, stressTest
} from '../core/risk-calc.js';
import { positionEventSignals } from './signals.js';
import { actionDirection } from '../core/signal-stance.js';

const TYPE_COL = { Stock: '#7ff0c4', ETF: '#8b5cf6', Fund: '#a78bfa', Commodity: '#FF8C42', Cash: '#4DC9F6', Crypto: '#D4A853', Other: '#9aa6c7' };
const FX_COL = { USD: '#6aa0ff', TRY: '#34e2a0', EUR: '#a78bfa' };

const empty = msg => `<div class="neo-empty">${esc(msg)}</div>`; // S2b: shared empty-state class
const ddTxt = v => v < 0 ? f(v, 1) + '%' : (v === 0 ? '0%' : 'At peak');

export function renderRisk() {
  const el = document.getElementById('riskBody');
  if (!el) return;

  const C = riskConcentration();
  const D = riskDrawdownVol();
  const T = riskOpenTrades();
  const X = riskFxExposure();
  const A = riskAllocationDrift();
  const SC = riskScore({ C, D, T, X });

  // event-risk count for the row summary (rendering stays delegated to the panel)
  let evCount = 0;
  try { evCount = (positionEventSignals() || []).length; } catch (_e) { /* optional */ }

  const row = (id, ac, title, sum, body, open) => `
    <div class="mny-row${open ? ' open' : ''}" id="${id}" style="--ac:${ac}">
      <div class="mny-rmain" data-action="mnyToggle" data-args='["${id}"]'>
        <span class="dot"></span><span class="rt">${title}</span>
        <span class="rsum">${sum}</span>
        <span class="chev">▾</span>
      </div>
      <div class="mny-rdetail"><div class="mny-rinner">${body}</div></div>
    </div>`;

  const topFx = X.breakdown[0];
  const topPos = C.positions[0];
  el.innerHTML = `
    <div class="mny-hd">
      <span class="mny-live"></span><h1 class="mny-h1" style="background:linear-gradient(90deg,#ff5470,#ff8c42,#e8b84b);-webkit-background-clip:text;background-clip:text">Risk</h1>
      <span class="mny-meta">5 domains · same formulas as the Advisory engine · nothing recalculated</span>
    </div>
    ${_heroRisk(SC, C, T, A)}
    <div class="grid4">
      <div class="panel kpi"><div class="lbl">Concentration</div><div class="v" style="color:${C.ratingColor}">${C.hhi ? Math.round(C.hhi) : '—'}</div><div class="ksub">${C.hhi ? 'HHI · ' + esc(C.rating.toLowerCase()) : 'no holdings'}</div></div>
      <div class="panel kpi"><div class="lbl">Max Drawdown</div><div class="v ${D.maxDD < -5 ? 'neg' : ''}">${D.snapCount > 1 ? ddTxt(D.maxDD) : '—'}</div><div class="ksub">${D.snapCount > 1 ? 'from peak' : 'no snapshots'}</div></div>
      <div class="panel kpi"><div class="lbl">Open Risk</div><div class="v neg">${T.rMultiples.length ? fU(T.totRisk) : '—'}</div><div class="ksub">${T.pctNW != null ? f(T.pctNW, 1) + '% of net worth' : 'no SL-defined trades'}</div></div>
      <div class="panel kpi"><div class="lbl">FX Top</div><div class="v" style="color:${topFx ? (FX_COL[topFx.ccy] || 'var(--t1)') : 'var(--t1)'}">${topFx ? esc(topFx.ccy) + ' ' + f(topFx.pct, 0) + '%' : '—'}</div><div class="ksub">currency weight</div></div>
    </div>
    <div class="mny-lbl">STRESS TEST · ALWAYS LIVE</div>
    <div class="mny-spanel" style="margin-bottom:26px">${_stressPanel()}</div>
    <div class="mny-qhd">DOMAINS · SUMMARY FIRST, DETAIL ONE CLICK AWAY</div>
    ${row('rkrow-conc', 'var(--red)', 'Concentration',
    topPos ? `top: <b>${esc(topPos.ticker)}</b> ${f(topPos.pct, 1)}% · HHI ${Math.round(C.hhi)}` : 'no holdings',
    _concentrationPanel(C), !!(SC.worst && SC.worst.key === 'concentration'))}
    ${row('rkrow-lev', 'var(--ora,#ff8c42)', 'Leverage & Open Trades',
    T.slLessLeveraged.length ? `<span class="neg">${T.slLessLeveraged.length} SL-less leveraged</span> · at-risk ${T.rMultiples.length ? fU(T.totRisk) : '—'}` : (T.rMultiples.length ? 'at-risk ' + fU(T.totRisk) : 'no open trade risk'),
    _leveragePanel(T), !!(SC.worst && SC.worst.key === 'leverage'))}
    ${row('rkrow-event', 'var(--pur,#a78bfa)', 'Event Risk',
    evCount ? `<span class="gold">${evCount} signal${evCount === 1 ? '' : 's'}</span> touching positions` : 'no position events',
    _eventRiskPanel(), false)}
    ${row('rkrow-dd', 'var(--yel)', 'Drawdown & Volatility',
    D.snapCount > 1 ? `current ${ddTxt(D.curDD)} · max ${ddTxt(D.maxDD)}` : 'needs snapshots',
    _drawdownPanel(D), !!(SC.worst && SC.worst.key === 'drawdown'))}
    ${row('rkrow-fx', 'var(--cyan,#00f0ff)', 'FX Exposure',
    topFx ? `${esc(topFx.ccy)} ${f(topFx.pct, 0)}% of exposure` : 'no exposure',
    _fxPanel(X), !!(SC.worst && SC.worst.key === 'fx'))}
    ${row('rkrow-drift', 'var(--grn)', 'Allocation Drift',
    A.rows.length ? (A.offCount ? `<span class="neg">${A.offCount} off-target</span>` : 'all on target') : 'no targets set',
    _driftPanel(A), false)}
    <div class="mny-foot">RISK IS A VETO · SAME FORMULAS AS THE ADVISORY ENGINE · NOTHING RECALCULATED</div>`;

  _wireStress();
}

// ===== Composite score hero (.neo-hero shell — number + band + domain bars + ring) =====
function _heroRisk(SC, C, T, A) {
  const off = Math.round(323 * (1 - SC.overall / 100));
  const chips =
    (SC.worst ? `<span class="neo-chip">Worst drag <b class="neg">${esc(SC.worst.label)} ${Math.round(SC.worst.score)}</b></span>` : '') +
    (C.hhi ? `<span class="neo-chip">HHI <b style="color:${C.ratingColor}">${Math.round(C.hhi)} · ${esc(C.rating.toUpperCase())}</b></span>` : '') +
    (T.rMultiples.length ? `<span class="neo-chip">Open risk <b class="neg">${fU(T.totRisk)}</b></span>` : '') +
    (A.rows.length ? `<span class="neo-chip">Drift <b class="${A.offCount ? 'neg' : 'pos'}">${A.offCount ? A.offCount + ' off' : 'on target'}</b></span>` : '');
  const bars = SC.components.map(c => {
    const sc = c.score == null ? null : Math.round(c.score);
    const col = sc == null ? 'var(--t3)' : sc >= 70 ? 'var(--grn)' : sc >= 40 ? 'var(--yel)' : 'var(--red)';
    return `<div class="rk-hb">
      <div class="r1"><span class="l">${esc(c.label)}</span><span class="v" style="color:${col}">${sc == null ? 'n/a' : sc}</span></div>
      <div class="tr"><i class="fl" style="width:${sc == null ? 0 : sc}%;background:${col}"></i></div>
      <div class="wt">${esc(c.note)} · weight ${Math.round(c.weight * 100)}%</div>
    </div>`;
  }).join('');
  return `<div class="neo-hero" style="--hx:#ff5470;border-color:rgba(255,84,112,.35)">
    <div class="neo-hleft">
      <div class="neo-hlbl">COMPOSITE RISK SCORE · 0-100 HEALTH</div>
      <div class="neo-hnum" style="color:${SC.color};text-shadow:0 0 22px color-mix(in srgb,${SC.color} 25%,transparent)">${SC.overall} <span style="font-size:15px;color:var(--t3);font-weight:500;letter-spacing:.1em">${esc(SC.band.toUpperCase())}</span></div>
      <div class="neo-chips">${chips}</div>
    </div>
    <div class="rk-hbars">${bars}</div>
    <div class="neo-ring"><svg width="124" height="124" viewBox="0 0 124 124">
      <defs><linearGradient id="rkGradR" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#ff5470"/><stop offset="100%" stop-color="#e8b84b"/></linearGradient></defs>
      <circle class="bgc" cx="62" cy="62" r="51.4"/>
      <circle class="fgc" cx="62" cy="62" r="51.4" style="stroke:url(#rkGradR);stroke-dashoffset:${off};stroke-dasharray:323"/></svg>
      <div class="rlbl"><b style="color:${SC.color}">${SC.overall}</b><span>${esc(SC.band.toUpperCase())}</span></div>
    </div>
  </div>`;
}

// ===== Stress-test (interactive, existing data only) =====
function _stressPanel() {
  return `<div class="panel" style="width:100%">
    <div style="font-size:10px;color:var(--t3);font-family:var(--mono);text-align:right;margin-bottom:4px">what-if · live data</div>
    <div class="rk-sliders">
      <div>
        <div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:6px"><span style="color:var(--t2)">Risk assets drop</span><span id="rkStAssetV" style="color:var(--red);font-weight:600;font-family:var(--mono)">20%</span></div>
        <input type="range" id="rkStAsset" min="0" max="60" value="20" step="1">
      </div>
      <div>
        <div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:6px"><span style="color:var(--t2)">TRY depreciates</span><span id="rkStTryV" style="color:var(--yel);font-weight:600;font-family:var(--mono)">20%</span></div>
        <input type="range" id="rkStTry" min="0" max="60" value="20" step="1">
      </div>
    </div>
    <div id="rkStOut"></div>
    <div style="font-size:10px;color:var(--t3);margin-top:10px">Leverage-aware. Asset drop hits non-cash holdings + open-trade underlyings; TRY shock hits TRY-denominated assets and cash.</div>
  </div>`;
}

function _stressOut(s) {
  const col = s.totalImpact < 0 ? 'var(--red)' : 'var(--grn)';
  const row = (label, loss) => `<div style="display:flex;justify-content:space-between;font-size:11px;padding:3px 0"><span style="color:var(--t3)">${label}</span><span class="${loss > 0 ? 'neg' : ''}" style="font-family:var(--mono)">${loss > 0 ? '−' + fU(loss) : fU(0)}</span></div>`;
  return `
    <div style="display:flex;justify-content:space-between;align-items:baseline;padding:8px 0;border-top:1px solid var(--bd);border-bottom:1px solid var(--bd)">
      <div><div style="font-size:10px;color:var(--t3)">Net worth impact</div><div style="font-family:var(--mono);font-size:22px;font-weight:700;color:${col}">${fU(s.totalImpact)}</div></div>
      <div style="text-align:right"><div style="font-size:10px;color:var(--t3)">${f(s.impactPct, 1)}%</div><div style="font-size:11px;color:var(--t3)">${fU(s.base)} → <b style="color:var(--t1)">${fU(s.newWealth)}</b></div></div>
    </div>
    <div style="margin-top:8px">
      ${row('Holdings drop', s.assetLoss)}
      ${row('Leverage amplification', s.leverageLoss)}
      ${row('TRY currency shock', s.tryLoss)}
    </div>`;
}

function _wireStress() {
  const a = document.getElementById('rkStAsset');
  const t = document.getElementById('rkStTry');
  const out = document.getElementById('rkStOut');
  if (!a || !t || !out) return;
  const run = () => {
    const av = +a.value, tv = +t.value;
    const av2 = document.getElementById('rkStAssetV'); if (av2) av2.textContent = av + '%';
    const tv2 = document.getElementById('rkStTryV'); if (tv2) tv2.textContent = tv + '%';
    out.innerHTML = _stressOut(stressTest(av, tv));
  };
  a.addEventListener('input', run);
  t.addEventListener('input', run);
  run();
}

// ===== 1. Concentration =====
function _concentrationPanel(C) {
  let body;
  if (!C.positions.length) body = empty('No priced holdings');
  else {
    const meterPct = Math.min(C.hhi / 4000 * 100, 100).toFixed(0);
    const bars = C.positions.slice(0, 5).map(p => {
      const col = TYPE_COL[p.type] || TYPE_COL.Other;
      return `<div class="crow"><div class="clbl">${esc(p.ticker)}</div><div class="ctrack"><div class="cfill" style="width:${Math.min(p.pct * 2.5, 100).toFixed(0)}%;background:${col}"></div></div><div class="cval">${f(p.pct, 1)}%</div></div>`;
    }).join('');
    const types = C.typeConc.slice(0, 6).map(t => {
      const col = TYPE_COL[t.type] || TYPE_COL.Other;
      const hot = t.pct > 50;
      return `<span class="gls-soft" style="display:inline-flex;align-items:center;gap:5px;font-size:11px;padding:4px 10px;border-radius:7px;border:1px solid var(--bd);border-left:3px solid ${col};color:${hot ? 'var(--yel)' : 'var(--t2)'}">${esc(t.type)} ${f(t.pct, 1)}%</span>`;
    }).join('');
    body = `
      <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:8px">
        <div style="font-family:var(--mono);font-size:24px;font-weight:700;color:${C.ratingColor}">${f(C.top5Pct, 1)}%</div>
        <div style="font-size:11px;color:var(--t3)">top-5 of value · HHI ${Math.round(C.hhi)} (${C.rating.toLowerCase()})</div>
      </div>
      <div class="gls-track" style="height:4px;border-radius:3px;overflow:hidden;margin-bottom:12px"><div style="width:${meterPct}%;height:100%;background:linear-gradient(90deg,#34e2a0,#e8b84b,#ff5470)"></div></div>
      ${bars}
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:12px">${types}</div>
      <div style="font-size:11px;color:var(--t3);margin-top:10px">Top-1 ${C.top1 ? esc(C.top1.ticker) + ' ' + f(C.top1Pct, 1) + '%' : '—'} · Top-3 ${f(C.top3Pct, 1)}%</div>`;
  }
  return `<div class="panel" style="width:100%">${body}</div>`;
}

// ===== 2. Drawdown & Volatility =====
function _drawdownPanel(D) {
  let body;
  if (D.snapCount < 2) body = empty('Need at least 2 daily snapshots');
  else {
    // S2b stat-cell pilot (StatCells Mockup v0.1 approved): .sc → .neo-cell.flat (dense grid variant)
    const _per = D.medianGapDays > 1.5 ? `per ${f(D.medianGapDays, 0)}d` : 'daily';
    const vol = D.dailyVol != null
      ? `<div class="neo-cell flat"><div class="l">Volatility</div><div class="v">${f(D.dailyVol, 2)}%</div><div class="s">${_per} · ${f(D.annualVol, 1)}% ann.</div></div>`
      : `<div class="neo-cell flat"><div class="l">Volatility</div><div class="v">—</div><div class="s">need 5+ snapshots</div></div>`;
    body = `
      <div class="sg" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));gap:8px">
        <div class="neo-cell flat"><div class="l">Max Drawdown</div><div class="v ${D.maxDD < -5 ? 'neg' : 'neu'}">${ddTxt(D.maxDD)}</div><div class="s">peak-to-trough wealth</div></div>
        <div class="neo-cell flat"><div class="l">Current DD</div><div class="v ${D.curDD < -3 ? 'neg' : 'neu'}">${D.curDD < 0 ? f(D.curDD, 1) + '%' : 'At peak'}</div><div class="s">vs peak wealth</div></div>
        ${vol}
      </div>
      <div style="font-size:11px;color:var(--t3);margin-top:10px">Peak wealth ${fU(D.peakVal)} · now ${fU(D.nw)} · ${D.snapCount} snapshots${D.medianGapDays > 1.5 ? `, median ${f(D.medianGapDays, 0)} days apart` : ''}. Annualised from the observed spacing, not a fixed √252.</div>
      <div style="font-size:11px;color:${D.basis === 'investment' ? 'var(--t3)' : 'var(--yel)'};margin-top:6px">${
        D.basis === 'investment'
          ? 'Volatility is measured on investment returns — deposits and withdrawals are removed.'
          : D.basis === 'mixed'
            ? `Partly deposit-adjusted (${D.adjustedPairs} of ${D.snapCount - 1} intervals). Older snapshots predate contribution tracking, so those intervals still read a payday as a gain.`
            : 'Deposit-inclusive: a salary or bonus reads as a positive return here, which inflates volatility and hides real drawdowns. Contribution tracking starts with today\'s snapshot.'
      }</div>`;
  }
  return `<div class="panel" style="width:100%">${body}</div>`;
}

// ===== 3. Leverage & Open-trade Risk =====
function _leveragePanel(T) {
  const flag = T.slLessLeveraged.length
    ? `<div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:rgba(255,84,112,.12);border-radius:6px;border-left:3px solid var(--red);font-size:12px;margin-bottom:12px;color:var(--red);font-weight:600">
        ⚠ ${T.slLessLeveraged.length} leveraged trade(s) with no stop-loss — uncapped downside</div>`
    : '';

  let gauge;
  if (!T.rMultiples.length) {
    gauge = empty('No open trades with TP1 + SL set');
  } else {
    const tot = (T.totRisk + T.totReward) || 1;
    const zero = 10, spanw = 88;
    const maxR = Math.max(...T.rMultiples.map(r => r.Rtp)) * 1.05 || 1;
    const rmRows = T.rMultiples.map(r => {
      const tpPos = Math.min(zero + (r.Rtp / maxR) * spanw, 98);
      const curClamp = Math.max(-0.5, Math.min(r.curR, r.Rtp));
      // floor at 0: with small maxR a -0.5R clamp maps left of the track and the fill
      // bleeds over the ticker labels (visible once #page-risk styling landed 2026-07-15)
      const curPos = Math.max(0, zero + (curClamp / maxR) * spanw);
      const col = r.curR >= 0 ? 'var(--grn)' : 'var(--red)';
      const left = r.curR >= 0 ? zero : Math.min(zero, curPos);
      const width = Math.abs(curPos - zero);
      const def = r.isDefSL ? '<span style="color:var(--t3)"> ·def SL</span>' : '';
      return `<div class="rmrow"><div class="rmtk">${esc(r.ticker)}<span>now ${r.curR >= 0 ? '+' : ''}${r.curR.toFixed(2)}R${def}</span></div>
        <div class="rmtrack"><div class="rmfill" style="left:${left}%;width:${width}%;background:${col}"></div><div class="rmzero" style="left:${zero}%"></div><div class="rmtp" style="left:${tpPos}%">▸TP1</div></div>
        <div class="rmval">${r.Rtp.toFixed(2)}R</div></div>`;
    }).join('');
    gauge = `
      <div class="riskNum">${fU(T.totRisk)}</div>
      <div class="riskSub">total at risk${T.pctNW != null ? ` · <b class="neg">${f(T.pctNW, 1)}%</b> of net worth` : ''}</div>
      <div class="rrbar"><div style="width:${(T.totRisk / tot * 100).toFixed(1)}%;background:var(--red)"></div><div style="width:${(T.totReward / tot * 100).toFixed(1)}%;background:var(--grn)"></div></div>
      <div class="riskKpi"><div class="rk"><div class="l">At risk</div><div class="v neg">${fU(T.totRisk)}</div></div><div class="rk"><div class="l">Reward @TP1</div><div class="v pos">${fU(T.totReward)}</div></div><div class="rk"><div class="l">R:R</div><div class="v" style="color:var(--cyan)">${T.rr.toFixed(2)}</div></div></div>
      <div style="margin-top:14px">${rmRows}</div>`;
  }

  const lev = T.levTrades.length
    ? `<div style="font-size:11px;color:var(--t3);margin-top:12px">Leverage exposure ${fU(T.levExposure)} across ${T.levTrades.length} trade(s)${T.slLessCount ? ` · ${T.slLessCount} open trade(s) without SL` : ''}</div>`
    : (T.slLessCount ? `<div style="font-size:11px;color:var(--t3);margin-top:12px">${T.slLessCount} open trade(s) without SL (default 25% used)</div>` : '');

  return `<div class="panel" style="width:100%">
    <div style="font-size:10px;color:var(--t3);font-family:var(--mono);text-align:right;margin-bottom:8px">if every SL hits</div>
    ${flag}${gauge}${lev}</div>`;
}

// ===== 4. FX Exposure =====
function _fxPanel(X) {
  const body = X.breakdown.length
    ? X.breakdown.map(r => `<div class="fxrow"><div class="fxlbl">${r.ccy}</div><div class="fxtrack"><div class="fxfill" style="width:${r.pct.toFixed(1)}%;background:${FX_COL[r.ccy] || '#9aa6c7'}"></div></div><div class="fxval">${r.pct.toFixed(0)}% · ${fU(r.usd)}</div></div>`).join('')
    : empty('No currency exposure');
  return `<div class="panel" style="width:100%">${body}
    <div style="font-size:11px;color:var(--t3);margin-top:10px">Holdings + cash + open-trade capital, converted to USD. Total ${fU(X.total)}.</div></div>`;
}

// ===== Event Risk (Signals → Risk bridge) =====
// Held positions with a triggering/approaching signal = event risk surfaced here.
// Reuses positionEventSignals() (same source as the Signals Action Queue). Drill = Signals page.
function _eventRiskPanel() {
  const ev = positionEventSignals();
  const head = `<div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:10px">
    <span style="font-size:12px;color:var(--t3)">${ev.items.length ? `${ev.triggered} triggered · ${ev.watch} watch` : 'position events'}</span>
    <a data-action="navTo" data-args='["signals"]' style="font-size:10px;color:var(--cyan);cursor:pointer;font-family:var(--mono)">Signals &rarr;</a>
  </div>`;
  let body;
  if (!ev.items.length) {
    body = empty('No triggering / approaching signals on your positions');
  } else {
    body = ev.items.slice(0, 6).map(({ sig, prox }) => {
      const trig = sig.status === 'TRIGGERED';
      const _rdir = actionDirection(sig.action);
  const col = _rdir === 'bull' ? '#3fb950' : _rdir === 'bear' ? '#f85149' : 'var(--bd)';
      const proxLab = trig
        ? '<span style="color:var(--red);font-weight:600">at level</span>'
        : (prox === Infinity ? '<span style="color:var(--t3)">no level</span>' : `<span style="color:var(--salmon)">${prox.toFixed(1)}% to level</span>`);
      return `<div class="gls-soft" style="display:flex;align-items:center;gap:10px;padding:8px 10px;border:1px solid var(--bd);border-left:3px solid ${col};border-radius:7px;margin-bottom:6px">
        <div style="flex:1;min-width:0">
          <div style="font-size:12px;font-weight:600;color:var(--t1)">${esc(sig.assetName || sig.ticker)} <span style="color:var(--acc);font-size:10px">${esc(sig.ticker)}</span></div>
          <div style="display:flex;gap:6px;align-items:center;margin-top:2px;font-size:10px"><span style="color:${col};font-weight:700">${esc(sig.action || '')}</span><span style="color:${trig ? 'var(--red)' : 'var(--salmon)'};font-weight:600">${trig ? 'TRIGGERED' : 'APPROACHING'}</span><span>${proxLab}</span></div>
        </div>
      </div>`;
    }).join('') + (ev.items.length > 6 ? `<div style="font-size:10px;color:var(--t3);margin-top:4px">+ ${ev.items.length - 6} more — see Signals</div>` : '');
  }
  return `<div class="panel" style="width:100%">${head}${body}</div>`;
}

// ===== 5. Allocation Drift =====
function _driftPanel(A) {
  let body;
  if (!A.rows.length) body = empty('No allocation targets set');
  else {
    const caveat = !A.targetsComplete
      ? `<div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:rgba(232,184,75,.12);border-radius:6px;border-left:3px solid var(--yel);font-size:11px;margin-bottom:12px;color:var(--yel)">
          Targets sum to ${f(A.targetSum, 0)}% (incomplete) — drift figures are indicative, not exact</div>`
      : '';
    const grid = A.rows.map(r => {
      const devColor = r.dev > A.threshold ? 'var(--grn)' : r.dev < -A.threshold ? 'var(--red)' : 'var(--t3)';
      const arrow = r.dev > 0.5 ? '▲' : r.dev < -0.5 ? '▼' : '';
      const col = TYPE_COL[r.type] || TYPE_COL.Other;
      const absD = Math.abs(r.dev);
      return `<div class="gls-soft" style="border:1px solid var(--bd);border-radius:10px;padding:8px 10px;border-left:3px solid ${r.off ? devColor : col}">
        <div style="font-size:11px;font-weight:600;color:${col}">${esc(r.type)}</div>
        <div style="display:flex;justify-content:space-between;margin-top:4px">
          <div style="font-size:10px;color:var(--t3)">Target<br><span style="color:var(--t1);font-size:12px;font-weight:600">${f(r.targetPct, 1)}%</span></div>
          <div style="font-size:10px;color:var(--t3)">Actual<br><span style="color:var(--t1);font-size:12px;font-weight:600">${f(r.actualPct, 1)}%</span></div>
        </div>
        <div style="font-size:11px;font-weight:600;color:${devColor};margin-top:4px;text-align:center">${arrow} ${r.dev > 0 ? '+' : ''}${f(r.dev, 1)}%</div>
        ${r.off ? `<div class="gls-track" style="margin-top:4px;height:3px;border-radius:2px;overflow:hidden"><div style="width:${Math.min(absD / A.threshold * 50, 100)}%;height:100%;background:${devColor};border-radius:2px"></div></div>` : ''}</div>`;
    }).join('');
    body = `${caveat}
      <div style="font-size:11px;color:${A.offCount ? 'var(--yel)' : 'var(--grn)'};margin-bottom:10px">${A.offCount ? `⚠ ${A.offCount} type(s) off-target (>${A.threshold}%)` : '✓ All within threshold'}</div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:8px">${grid}</div>`;
  }
  return `<div class="panel" style="width:100%">${body}</div>`;
}
